/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2022, Bulb Inc. For more information, please email contact@hellobulb.com.
*/
import { isObservable, Observable } from 'rxjs';
import { Inject, Injectable } from '@angular/core';
import { SaveStateStore, SaveStatus } from './save-state.store';
import { ErrorService } from '../common/error.service';
import { BULB_BUSY_COVER_SERVICE } from '../app/angularjs.providers';

/**
 * Interface to be implemented by users of this service for handling the actual saves.
 */
export interface SaveAdapter {
  /**
   * Method that actually performs the save, returning either a promise or observable which resolves to a result or
   * an error when the save completes.
   */
  save() : Observable<any> | Promise<any>;

  /**
   * Whether the busy indicator should always be shown when doing the save.  If false, the busy indicator will only
   * be shown if another save is already in progress.  If true, then the busy indicator will always be displayed.
   */
  showBusy : boolean;

  /**
   * Whether to prevent the automatic retry of a failed save.  Typically, used if there may be client/validation errors
   * that need to be handled by the client/user, where an automatic retry is unnecessary.
   */
  preventRetry : boolean;
}

/**
 * Service designed to serialize saves.
 *
 * The purpose is to make sure there aren't race conditions based on a save being sent to a server before the previous
 * save has finished resolving.
 */
@Injectable()
export class SerialSaveService {

  private prevSavePromise: Promise<any> = null;

  /**
   * Default Constructor.
   */
  constructor(
    @Inject(BULB_BUSY_COVER_SERVICE) private bulbBusyCoverService,
    private saveStateStore: SaveStateStore,
    private errorService: ErrorService,
  ) { }

  /**
   * Request a serialized save to occur.
   *
   * All the saves occur in the order they are requested.
   *
   * When a save reaches the front of the queue, this service calls the save() method of the provided SaveAdapter.
   * If the save fails, the service will automatically retry the save 1 time.
   *
   * A busy indicator will be displayed if a save is requested while a previous save is still processing.
   *
   * If the SaveAdapter.showBusy parameter is set to true, the busy indicator will be displayed during the save
   * regardless whether a previous save is active.
   *
   * Because of the auto-retry, it is preferred for a caller to not perform actions as a results of the save callback
   * promise being resolved or rejected, but rather wait on the promise returned from this method.
   *
   * Example:
   * <code>
   *     SerialSaveService.save({
   *       showBusy: true,
   *       save: () => Restangular.one('units', unit.id).remove()
   *     });
   * </code>
   *
   * @param saveAdapter Adapter which provides a callback function for actually performing the save.
   *
   * @return A promise that will be resolved when the save is successful, or rejected if the save fails twice.
   */
  public save(saveAdapter : SaveAdapter) : Promise<any> {

    let prevSave = this.prevSavePromise;
    let showBusy = saveAdapter.showBusy || false;
    const preventRetry = saveAdapter.preventRetry || false;

    if (prevSave === null) {
      // No ongoing save, so we don't have to wait for anything, so create a completed observable
      prevSave = Promise.resolve();
    } else {
      // There is an ongoing save, and we are queuing a new save, so put up the busy indicator:
      showBusy = true;
    }

    const newSavePromise = new Promise((resolve, reject) => {

      // wait for previous save to complete
      prevSave.finally(() => {

        // update the status and perform the save:
        this.saveStateStore.setStatus(SaveStatus.SAVING);

        // Call the save
        const savePromise : Promise<any> = SerialSaveService.callAdapterSave(saveAdapter);

        // handle response
        savePromise.then(async (result) => {
          await resolve(result);
          this.saveStateStore.setStatus(SaveStatus.SAVED);
        }).catch((error) => {
          if (preventRetry) {
            reject(error);
          } else {
            this.retrySave(saveAdapter, resolve, reject);
          }
        });
      });
    });

    // Update busy indicator
    if (showBusy) {
      this.bulbBusyCoverService.showBusyDuring(newSavePromise);
    }

    // This new save is now the prev save for the next caller
    this.prevSavePromise = newSavePromise;

    // Reset the previous save reference if it hasn't changed and this save has finished.
    newSavePromise.finally(() => {
      if (this.prevSavePromise === newSavePromise) {
        this.prevSavePromise = null;
      }
    });

    return newSavePromise;
  }

  /**
   * Helper method to make the call to the save callback.
   *
   * @param saveAdapter Adapter containing the save callback method.
   * @private
   */
  private static callAdapterSave(saveAdapter : SaveAdapter) : Promise<any> {
    // Call the save
    const saveResponse = saveAdapter.save() as Observable<any>;

    // Make sure the response is a promise or an observable converted to a promises to simplify processing
    if (isObservable(saveResponse)) {
      return saveResponse.toPromise();
    }

    // TODO CG:
    return saveResponse as any as Promise<any>;
  }

  /**
   * Helper method to retry the save if the first attempt results in an error.
   *
   * @param saveAdapter Adapter containing the save callback.
   * @param resolve resolve method for the original save promise.
   * @param reject reject method for the original save promise.
   * @private
   */
  private retrySave(saveAdapter : SaveAdapter, resolve, reject) : void {

    this.saveStateStore.setStatus(SaveStatus.RETRYING_SAVE);

    const savePromise : Promise<any> = SerialSaveService.callAdapterSave(saveAdapter);

    this.bulbBusyCoverService.showBusyDuring(savePromise.then(
      (result) => {
        this.saveStateStore.setStatus(SaveStatus.SAVED);
        resolve(result);
      },
      (errorOnRetry) => {
        if (errorOnRetry.status === 400 && errorOnRetry.data?.developerMessage === 'Revisions are not matched.') {
          this.saveStateStore.setStatus(SaveStatus.REVISION_MISMATCH);
        } else {
          this.saveStateStore.setStatus(SaveStatus.ERROR);
        }
        // Capture the error.  We know it is a save, so we want to capture more errors than our built-in
        // restangular error interceptors.
        // Handle a few specific errors differently, so they are categorized better in sentry.
        if (errorOnRetry.data?.developerMessage === 'Revisions are not matched.') {
          this.errorService.captureErrorMessage('Revisions are not matched.', errorOnRetry);
        } else if (errorOnRetry.data?.developerMessage.includes('Your org administrator has disabled')
          || errorOnRetry.data?.developerMessage.includes('User does not have permission to share to')) {
          this.errorService.captureErrorMessage('Invalid Share Request. '
            + 'User attempted to share to entity that was prohibited.', errorOnRetry);
        } else if (errorOnRetry.data?.developerMessage !== 'server_error_body_code') {
          this.errorService.captureAnyError(errorOnRetry, true);
        }
        reject(errorOnRetry);
      },
    ));
  }
}
